<template>
  <div class="player">
    <div class="player-controls">
      <div class="play">
        <img
          :src="imagePlayPauseSource"
          alt="Play"
          @click.prevent="playing = !playing"
          :title="playing ? 'Pause' : 'Play'"
          class="control-button"
        />
      </div>
      <div class="player-time">
        <div class="player-time-current">
          {{ convertTimeHHMMSS(currentSeconds) }}
        </div>
        <div class="seek">
          <div class="player-timeline">
            <div :style="progressStyle" class="player-progress"></div>
            <div v-on:click="seek" class="player-seeker" title="Seek"></div>
          </div>
        </div>
        <div class="player-time-total" v-if="currentSeconds > 0">
          -{{ convertTimeHHMMSS(durationLeft) }}
        </div>
        <div class="player-time-total" v-else>
          -{{ convertTimeHHMMSS(durationSeconds) }}
        </div>
      </div>
      <div id="full-screen" class="full-screen-button">
        <img
          :src="imageFullScreenSource"
          alt="FullScreen"
          class="control-button"
          @click.prevent="fullScreen"
        />
      </div>
      <div id="volume">
        <img
          :src="imageSoundOnSoundOffSource"
          alt="Volume"
          class="control-button"
          @click.prevent="mute"
          :title="volumeTitle"
        />
      </div>
    </div>
    <video
      ref="videoRef"
      @timeupdate="update"
      @loadeddata="load"
      @pause="playing = false"
      @play="playing = true"
      preload="auto"
      width="720"
      height="500"
      class="video"
    >
      <source :src="url" :type="type" />
    </video>
  </div>
</template>
<script>
import { computed, watch, ref } from "vue";

export default {
  name: "AppVideo",
  props: {
    autoPlay: {
      type: Boolean,
      default: false,
    },
    url: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    const currentSeconds = ref(0);
    const durationSeconds = ref(0);
    const durationLeft = ref(0);
    const loaded = ref(false);
    const playing = ref(false);
    const previousVolume = ref(35);
    const showVolume = ref(false);
    const volume = ref(100);
    const videoRef = ref("video");
    const imagePlayPauseSource = computed(() =>
      playing.value
        ? require(`@/Common/Icons/PauseButtonNew.png`)
        : require(`@/Common/Icons/PlayButtonNew.png`)
    );
    const imageSoundOnSoundOffSource = computed(() =>
      volume.value > 0
        ? require(`@/Common/Icons/SoundOnButtonNew.png`)
        : require(`@/Common/Icons/SoundOffButtonNew.png`)
    );

    const imageFullScreenSource = computed(() =>
      require(`@/Common/Icons/FullScreenButton.png`)
    );

    const muted = computed(() => volume.value === 0);

    const percentComplete = computed(() =>
      parseInt((currentSeconds.value / durationSeconds.value) * 100)
    );
    const progressStyle = computed(() => ({
      width: `${percentComplete.value}%`,
    }));
    const volumeTitle = computed(() => {
      return `Volume (${volume.value}%)`;
    });

    watch(playing, (newValue) => {
      newValue ? videoRef.value.play() : videoRef.value.pause();
    });
    watch(volume, (newValue) => {
      videoRef.value.volume = newValue / 100;
    });

    const convertTimeHHMMSS = (val) => {
      let hhmmss = new Date(val * 1000).toISOString().substr(11, 8);
      return hhmmss.indexOf("00:") === 0 ? hhmmss.substr(3) : hhmmss;
    };
    const load = () => {
      if (videoRef.value.readyState >= 2) {
        loaded.value = true;
        durationSeconds.value = parseInt(videoRef.value.duration);
        return (playing.value = props.autoPlay.value);
      }
      throw new Error("Failed to load sound file.");
    };
    const mute = () => {
      if (muted.value) {
        return (volume.value = previousVolume.value);
      }
      previousVolume.value = volume.value;
      volume.value = 0;
    };

    const fullScreen = () => {
      videoRef.value.requestFullscreen();
    };

    const seek = (e) => {
      if (!loaded.value) return;

      const bounds = e.target.getBoundingClientRect();
      const seekPos = (e.clientX - bounds.left) / bounds.width;

      videoRef.value.currentTime = parseInt(videoRef.value.duration * seekPos);
    };
    const stop = () => {
      playing.value = false;
      videoRef.value.currentTime = 0;
    };
    const update = () => {
      currentSeconds.value = parseInt(videoRef.value.currentTime);
      durationLeft.value = parseInt(
        videoRef.value.duration - videoRef.value.currentTime
      );
    };
    return {
      currentSeconds,
      durationSeconds,
      durationLeft,
      loaded,
      playing,
      previousVolume,
      showVolume,
      volume,
      muted,
      percentComplete,
      progressStyle,
      volumeTitle,
      videoRef,
      convertTimeHHMMSS,
      load,
      mute,
      seek,
      stop,
      update,
      fullScreen,
      imagePlayPauseSource,
      imageSoundOnSoundOffSource,
      imageFullScreenSource,
    };
  },
};
</script>
<style lang="scss" scoped src="@/Common/Styles/appVideo.scss"></style>
